/**
 * Cart Countdown Announcement
 * ADDED: 2026-07-27 by MeshPlanet
 *
 * <mp-cart-timer> renders a countdown whose deadline lives in a cookie, so the
 * same countdown keeps running across page loads and across cart drawer
 * re-renders (the drawer is morphed on every cart update, which re-connects
 * this element — connectedCallback simply re-reads the cookie).
 *
 * The countdown is illustrative: it does not create, apply or validate any
 * discount. Nothing here talks to the cart or the checkout.
 *
 * Data attributes:
 *   data-duration   total countdown length, in seconds
 *   data-on-expire  'restart' (start a fresh countdown) | 'hide' (hide for good)
 *   data-cookie     cookie name used to persist the deadline
 */
(function () {
  'use strict';

  /* Marks a countdown that already finished while on-expire is 'hide'. */
  var EXPIRED = 'expired';
  /* How long the "already finished" state is remembered, in seconds. */
  var EXPIRED_TTL = 60 * 60 * 24 * 7;

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; path=/; max-age=' +
      Math.max(1, Math.round(maxAgeSeconds)) +
      '; SameSite=Lax';
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  class MpCartTimer extends HTMLElement {
    connectedCallback() {
      this.duration = parseInt(this.dataset.duration, 10) || 0;
      this.onExpire = this.dataset.onExpire === 'hide' ? 'hide' : 'restart';
      this.cookieName = this.dataset.cookie || 'mp_cart_timer';

      this.hoursEl = this.querySelector('[data-timer-hours]');
      this.minutesEl = this.querySelector('[data-timer-minutes]');
      this.secondsEl = this.querySelector('[data-timer-seconds]');

      if (!this.duration || !this.secondsEl) return;

      var stored = readCookie(this.cookieName);

      if (stored === EXPIRED) {
        this.expire();
        return;
      }

      var deadline = parseInt(stored, 10);
      if (!deadline || deadline <= Date.now()) {
        deadline = this.startNewCountdown();
      }

      this.deadline = deadline;
      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    /* Cookie lifetime matches the countdown, so it disappears on its own. */
    startNewCountdown() {
      var deadline = Date.now() + this.duration * 1000;
      writeCookie(this.cookieName, String(deadline), this.duration);
      return deadline;
    }

    expire() {
      clearInterval(this.interval);
      this.hidden = true;
      writeCookie(this.cookieName, EXPIRED, EXPIRED_TTL);
    }

    tick() {
      var remaining = Math.floor((this.deadline - Date.now()) / 1000);

      if (remaining <= 0) {
        if (this.onExpire === 'hide') {
          this.expire();
          return;
        }
        this.deadline = this.startNewCountdown();
        remaining = this.duration;
      }

      var hours = Math.floor(remaining / 3600);
      var minutes = Math.floor((remaining % 3600) / 60);
      var seconds = remaining % 60;

      if (this.hoursEl) this.hoursEl.textContent = String(hours);
      if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
      this.secondsEl.textContent = pad(seconds);
    }
  }

  if (!customElements.get('mp-cart-timer')) {
    customElements.define('mp-cart-timer', MpCartTimer);
  }
})();
